import { Button, Textarea } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  NewTabLink,
  NotPermitted
} from '@/design';
import { ApiError } from '@/domains/api';
import { useCan } from '@/domains/auth';
import { internalFieldsApi } from '../../api/internal-fields-api';
import type { IListRow } from '../../api/lists-contracts';
import { LoadError } from '../../components/admin-page';
import { NotesLog } from '../../components/notes-log';
import { recordSurveyPath } from '../../components/participant-link';
import { listSurveyLabel, notesLogCode } from '../../lists';
import { refusalMessage } from '../../refusal';
import { nameOf } from './editing';
import type { IAdoptedCell } from './use-list-rows';

/**
 * The row's survey notes log from the list (SR-491, Conga's notes from the grid; SR-494): *Note* in
 * the Action column opens the log of the row's survey — its `<survey>.notes` staff field, the one
 * Lists, reports and Reinstatement's sit-out rule read — each entry's initials named on hover, and a
 * box that adds an entry. **survey-api dates and signs the entry** with the signed-in account's
 * initials (`internalFieldsApi.appendEntry`) and puts it at the log's head, so it reads as the
 * migrated entries do; the cell the grid holds takes the answer at once (`onAppended`).
 *
 * The log is read as stored (the field's history, `stored`) rather than off the row: the view need
 * not show the Notes column. Its code is the row survey's own (`notesLogCode`), not the screen's
 * catalogue's — *All surveys* carries no staff fields — and a survey whose catalogue has no notes
 * log says so (the read's 404).
 */
/** The log's two states besides its text: the survey has no notes field; the read failed. */
const NO_LOG = Symbol('no notes log');
const UNREAD = Symbol('unread');

export function RowNotesDialog({
  row,
  onAppended,
  onClose
}: {
  row: IListRow;
  onAppended: (cell: IAdoptedCell) => void;
  onClose: () => void;
}) {
  const permitted = useCan()('internal_fields.write');
  const reasonId = React.useId();
  /** The log: undefined while it is read, null where none is stored, `NO_LOG` where the survey
   *  has no notes field, `UNREAD` where the read failed. */
  const [log, setLog] = React.useState<Nullable<string> | undefined | typeof NO_LOG | typeof UNREAD>(
    undefined
  );
  const [error, setError] = React.useState<Nullable<string>>(null);
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  // An entry that landed is the log now: a first read answering after it is not drawn over it.
  const appended = React.useRef(false);
  const name = nameOf(row);
  const code = notesLogCode(row.survey);

  React.useEffect(() => {
    if (code === null) return;
    let live = true;
    internalFieldsApi.history(row.participantId, code, 1, 1).then(
      (history) => {
        if (!live || appended.current) return;
        // The response's own value where the field is kept per response, else the survey's.
        const stored =
          history.stored.find((one) => one.responseId === row.responseId) ??
          history.stored.find((one) => one.responseId === null);
        setLog(typeof stored?.value.v === 'string' ? stored.value.v : null);
      },
      (refused: unknown) => {
        if (!live || appended.current) return;
        setLog(refused instanceof ApiError && refused.status === 404 ? NO_LOG : UNREAD);
      }
    );
    return () => {
      live = false;
    };
  }, [code, row.participantId, row.responseId]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (code === null) return;
    setBusy(true);
    setError(null);
    try {
      const stored = await internalFieldsApi.appendEntry(row.responseId, code, text.trim());
      const v = typeof stored.value.v === 'string' ? stored.value.v : null;
      appended.current = true;
      setLog(v);
      setText('');
      onAppended({
        participantId: row.participantId,
        survey: row.survey,
        field: code,
        cell: v === null ? null : { v, version: stored.version }
      });
    } catch (refused) {
      // The typing stays in the box: a refused entry is not a lost one.
      setError(refusalMessage(refused, 'The entry could not be added.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Notes on {name}</DialogTitle>
          <DialogDescription>{listSurveyLabel(row.survey)}</DialogDescription>
        </DialogHeader>
        <DialogBody className="max-h-[70dvh] gap-4 overflow-y-auto text-sm">
          {code === null || log === NO_LOG ? (
            <p className="text-muted-foreground">This survey keeps no notes log.</p>
          ) : (
            <>
              {error && <LoadError message={error} />}
              <form className="grid gap-2" onSubmit={submit}>
                <Textarea
                  autoFocus
                  rows={3}
                  aria-label="New entry"
                  placeholder="Dated and signed with your initials"
                  value={text}
                  disabled={!permitted}
                  aria-describedby={permitted ? undefined : reasonId}
                  onChange={(event) => setText(event.target.value)}
                />
                {!permitted && <NotPermitted id={reasonId} what="adding to the notes" />}
                <span className="flex justify-end">
                  <Button type="submit" disabled={!permitted || busy || text.trim() === ''}>
                    {busy ? 'Adding…' : 'Add entry'}
                  </Button>
                </span>
              </form>
              {log === UNREAD ? (
                <LoadError message="The notes could not be read — close and open them again." />
              ) : log === undefined ? (
                <p className="text-muted-foreground">Reading the notes…</p>
              ) : log === null || log.trim() === '' ? (
                <p className="text-muted-foreground">No entry yet.</p>
              ) : (
                <NotesLog notes={log} startOpen />
              )}
            </>
          )}
          <NewTabLink
            to={recordSurveyPath(row.participantId, row.survey)}
            label="Open the record page in a new tab"
            className="justify-self-start px-0"
          >
            Open the record page
          </NewTabLink>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
